import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Loader2, Send, Sparkles, Check, ExternalLink } from "lucide-react";
import { authClient, docsClient } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { generateDocumentHash } from "@/lib/hash";

interface Message {
  role: "user" | "assistant";
  content: string;
  type?: "suggestion" | "question" | "message" | "fix" | "update-tags";
  documents?: DocumentSuggestion[];
  tag_updates?: TagUpdateSuggestion[];
  results?: {
    successful: number;
    failed: number;
    errors: string[];
  };
  jobs_created?: number;
}

interface DocumentSuggestion {
  url: string;
  name: string;
  description: string;
  tags: string[];
  confidence: number;
  reasoning: string;
}

interface TagUpdateSuggestion {
  id: string;
  title: string;
  current_tags: string[];
  suggested_tags: string[];
  reasoning: string;
  confidence: number;
  action: "replace" | "add";
}

interface AIAddDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDocumentAdded: () => void;
}

export default function AIAddDocumentDialog({
  open,
  onOpenChange,
  onDocumentAdded,
}: AIAddDocumentDialogProps) {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content:
        'Hi! I\'m here to help you find and add documentation to your team\'s library.\n\n💡 You can:\n• Request bulk downloads: "Download all MUI documentation"\n• Find specific docs: "FastAPI deployment guides"\n• Auto-discover: "Find comprehensive React docs"\n\nI\'ll automatically filter out documentation you already have. What are you looking for?',
      type: "message",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingDocs, setAddingDocs] = useState<Set<string>>(new Set());
  const [addingAll, setAddingAll] = useState(false);
  const { toast } = useToast();

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    // Add user message to chat
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);

    try {
      // Call Edge Function - refresh JWT to ensure it's not expired
      const {
        data: { session },
        error: sessionError,
      } = await authClient.auth.refreshSession();

      if (sessionError || !session) {
        throw new Error(sessionError?.message || "Failed to refresh session");
      }

      const response = await fetch(
        `${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/ai-document-discovery`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: userMessage,
            user_id: session?.user?.id,
            conversationHistory: messages
              .filter((m) => m.role === "user" || m.role === "assistant")
              .map((m) => ({
                role: m.role,
                content: m.content,
              })),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get AI response");
      }

      const data = await response.json();
      const aiResponse = data.response;

      // Add assistant message to chat
      if (aiResponse.type === "suggestion" && aiResponse.documents) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `I found ${aiResponse.documents.length} documentation source(s) for you. Review them below and click "Add to Library" for the ones you want.`,
            type: "suggestion",
            documents: aiResponse.documents,
          },
        ]);
      } else if (aiResponse.type === "fix") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: aiResponse.message,
            type: "fix",
            jobs_created: aiResponse.jobs_created,
          },
        ]);
        // Show success toast
        toast({
          title: "Fix Queued",
          description: `${aiResponse.jobs_created} job(s) created to re-download documents. Check the Jobs page for progress.`,
        });
      } else if (aiResponse.type === "update-tags") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              aiResponse.message ||
              `I found ${aiResponse.documents.length} document(s) that should have updated tags. Review and approve below.`,
            type: "update-tags",
            tag_updates: aiResponse.documents,
          },
        ]);
      } else if (aiResponse.type === "question") {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: aiResponse.message,
            type: "question",
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: aiResponse.message || aiResponse,
            type: "message",
          },
        ]);
      }
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description: "Failed to get AI response. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addDocument = async (doc: DocumentSuggestion, skipToast = false) => {
    setAddingDocs((prev) => new Set(prev).add(doc.url));

    try {
      // Get user from Agency-Auth SSO
      const {
        data: { user },
      } = await authClient.auth.getUser();

      if (!user) {
        throw new Error("Not authenticated");
      }

      // Check if URL already exists - use docsClient for data operations
      const { data: existing } = await docsClient
        .from("documents")
        .select("id, title, url")
        .eq("user_id", user.id)
        .eq("url", doc.url)
        .single();

      if (existing) {
        if (!skipToast) {
          toast({
            title: "Already exists",
            description: `"${existing.title}" is already in your library`,
            variant: "default",
          });
        }
        setAddingDocs((prev) => {
          const next = new Set(prev);
          next.delete(doc.url);
          return next;
        });
        return { success: false, reason: "duplicate" };
      }

      // Generate SHA-256 hash from URL
      const hash = await generateDocumentHash(doc.url);

      const { data: insertedDoc, error } = await docsClient
        .from("documents")
        .insert({
          url: doc.url,
          title: doc.name,
          description: doc.description,
          tags: doc.tags,
          user_id: user.id,
          hash: hash,
          processing_status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      if (!skipToast) {
        toast({
          title: "Success",
          description: `Added "${doc.name}" to your library. Content is being downloaded...`,
        });
      }

      // Trigger background processing
      if (insertedDoc) {
        // Get JWT from Agency-Auth SSO for edge function authorization
        const {
          data: { session },
        } = await authClient.auth.getSession();
        fetch(`${import.meta.env.VITE_DOCS_SUPABASE_URL}/functions/v1/process-document`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            document_id: insertedDoc.id,
          }),
        }).catch((err) => {
          console.error("Background processing failed:", err);
        });
      }

      if (!skipToast) {
        onDocumentAdded();
      }

      return { success: true };
    } catch (error) {
      console.error("Error adding document:", error);
      if (!skipToast) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to add document",
          variant: "destructive",
        });
      }
      return { success: false, reason: "error", error };
    } finally {
      setAddingDocs((prev) => {
        const next = new Set(prev);
        next.delete(doc.url);
        return next;
      });
    }
  };

  const addAllDocuments = async (docs: DocumentSuggestion[]) => {
    setAddingAll(true);
    let added = 0;
    let skipped = 0;
    let failed = 0;

    for (const doc of docs) {
      const result = await addDocument(doc, true);
      if (result?.success) {
        added++;
      } else if (result?.reason === "duplicate") {
        skipped++;
      } else {
        failed++;
      }
    }

    toast({
      title: "Bulk Add Complete",
      description: `Added ${added} documents. Skipped ${skipped} duplicates. ${failed > 0 ? `Failed: ${failed}` : ""}`,
    });

    setAddingAll(false);
    onDocumentAdded();
  };

  const applyTagUpdate = async (update: TagUpdateSuggestion, skipToast = false) => {
    setAddingDocs((prev) => new Set(prev).add(update.id));

    try {
      const {
        data: { user },
      } = await authClient.auth.getUser();

      if (!user) throw new Error("Not authenticated");

      // Get current document tags
      const { data: doc, error: docFetchError } = await docsClient
        .from("documents")
        .select("tags, user_id")
        .eq("id", update.id)
        .single();

      if (docFetchError || !doc) {
        console.error("Document fetch error:", docFetchError);
        console.error("Looking for document ID:", update.id);
        console.error("Current user ID:", user.id);
        throw new Error(`Document not found: ${docFetchError?.message || "Unknown error"}`);
      }

      // Verify user owns this document
      if (doc.user_id !== user.id) {
        throw new Error("You don't have permission to update this document");
      }

      // Determine final tags based on action
      let finalTags = update.suggested_tags;
      if (update.action === "add") {
        finalTags = [...new Set([...(doc.tags || []), ...update.suggested_tags])];
      }

      // Update tags using batch function
      // @ts-expect-error - Supabase generated types issue
      const { error } = await docsClient.rpc("batch_update_tags", {
        p_document_ids: [update.id],
        p_tags: finalTags,
      });

      if (error) throw error;

      if (!skipToast) {
        toast({
          title: "Success",
          description: `Updated tags for "${update.title}"`,
        });
        onDocumentAdded();
      }

      return { success: true };
    } catch (error) {
      console.error("Error updating tags:", error);
      if (!skipToast) {
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to update tags",
          variant: "destructive",
        });
      }
      return { success: false, reason: "error", error };
    } finally {
      setAddingDocs((prev) => {
        const next = new Set(prev);
        next.delete(update.id);
        return next;
      });
    }
  };

  const applyAllTagUpdates = async (updates: TagUpdateSuggestion[]) => {
    setAddingAll(true);
    let updated = 0;
    let failed = 0;

    for (const update of updates) {
      const result = await applyTagUpdate(update, true);
      if (result?.success) {
        updated++;
      } else {
        failed++;
      }
    }

    toast({
      title: "Bulk Tag Update Complete",
      description: `Updated ${updated} documents. ${failed > 0 ? `Failed: ${failed}` : ""}`,
    });

    setAddingAll(false);
    onDocumentAdded();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            AI Document Discovery
          </DialogTitle>
          <DialogDescription>
            Describe what documentation you're looking for, and I'll help you find it
          </DialogDescription>
        </DialogHeader>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-6 min-h-0">
          <div className="space-y-4 py-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.role === "assistant" ? (
                  <div className="max-w-[85%] space-y-2">
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="text-sm whitespace-pre-wrap">
                        {typeof message.content === "string"
                          ? message.content
                          : typeof message.content === "object" && message.content !== null
                            ? (message.content as { message?: string }).message ||
                              JSON.stringify(message.content, null, 2)
                            : String(message.content)}
                      </p>
                    </div>

                    {/* Document Suggestions */}
                    {message.documents && message.documents.length > 0 && (
                      <div className="space-y-2">
                        {/* Add All Button */}
                        {message.documents.length > 1 && (
                          <Button
                            onClick={() => addAllDocuments(message.documents!)}
                            disabled={addingAll || addingDocs.size > 0}
                            variant="default"
                            className="w-full"
                          >
                            {addingAll ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Adding {message.documents.length} documents...
                              </>
                            ) : (
                              <>
                                <Check className="mr-2 h-4 w-4" />
                                Add All {message.documents.length} Documents
                              </>
                            )}
                          </Button>
                        )}

                        {message.documents.map((doc, docIndex) => (
                          <Card key={docIndex} className="border-primary/20">
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base flex items-center gap-2">
                                    {doc.name}
                                    <Badge variant="outline" className="text-xs">
                                      {Math.round(doc.confidence * 100)}% match
                                    </Badge>
                                  </CardTitle>
                                  <CardDescription className="mt-1 text-xs line-clamp-2">
                                    {doc.description}
                                  </CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="pb-3">
                              <div className="flex flex-wrap gap-1 mb-2">
                                {doc.tags.map((tag) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <ExternalLink className="h-3 w-3" />
                                <a
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="hover:underline truncate"
                                >
                                  {doc.url}
                                </a>
                              </div>
                              {doc.reasoning && (
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  💡 {doc.reasoning}
                                </p>
                              )}
                            </CardContent>
                            <CardFooter className="pt-0">
                              <Button
                                onClick={() => addDocument(doc)}
                                disabled={addingDocs.has(doc.url)}
                                size="sm"
                                className="w-full"
                              >
                                {addingDocs.has(doc.url) ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Adding...
                                  </>
                                ) : (
                                  <>
                                    <Check className="mr-2 h-4 w-4" />
                                    Add to Library
                                  </>
                                )}
                              </Button>
                            </CardFooter>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Tag Update Suggestions */}
                    {message.tag_updates && message.tag_updates.length > 0 && (
                      <div className="space-y-2">
                        {/* Apply All Button */}
                        {message.tag_updates.length > 1 && (
                          <Button
                            onClick={() => applyAllTagUpdates(message.tag_updates!)}
                            disabled={addingAll || addingDocs.size > 0}
                            variant="default"
                            className="w-full"
                          >
                            {addingAll ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Updating {message.tag_updates.length} documents...
                              </>
                            ) : (
                              <>
                                <Check className="mr-2 h-4 w-4" />
                                Apply All Tag Updates
                              </>
                            )}
                          </Button>
                        )}

                        {message.tag_updates.map((update, updateIndex) => (
                          <Card key={updateIndex} className="border-amber-500/20 bg-amber-500/5">
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                                    {update.title}
                                    <Badge variant="outline" className="text-xs">
                                      {Math.round(update.confidence * 100)}% confidence
                                    </Badge>
                                    <Badge
                                      variant={
                                        update.action === "replace" ? "destructive" : "default"
                                      }
                                      className="text-xs"
                                    >
                                      {update.action === "replace" ? "Replace All" : "Add Tags"}
                                    </Badge>
                                  </CardTitle>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="pb-3">
                              {/* Current Tags */}
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-muted-foreground mb-1">
                                  Current Tags:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {update.current_tags.length > 0 ? (
                                    update.current_tags.map((tag) => (
                                      <Badge key={tag} variant="secondary" className="text-xs">
                                        {tag}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-xs text-muted-foreground italic">
                                      No tags
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Suggested Tags */}
                              <div className="mb-2">
                                <p className="text-xs font-semibold text-muted-foreground mb-1">
                                  Suggested Tags:
                                </p>
                                <div className="flex flex-wrap gap-1">
                                  {update.suggested_tags.map((tag) => (
                                    <Badge key={tag} variant="default" className="text-xs">
                                      {tag}
                                    </Badge>
                                  ))}
                                </div>
                              </div>

                              {/* Reasoning */}
                              {update.reasoning && (
                                <p className="text-xs text-muted-foreground mt-2 italic">
                                  💡 {update.reasoning}
                                </p>
                              )}
                            </CardContent>
                            <CardFooter className="pt-0">
                              <Button
                                onClick={() => applyTagUpdate(update)}
                                disabled={addingDocs.has(update.id)}
                                size="sm"
                                className="w-full"
                              >
                                {addingDocs.has(update.id) ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Updating...
                                  </>
                                ) : (
                                  <>
                                    <Check className="mr-2 h-4 w-4" />
                                    Apply Tag Update
                                  </>
                                )}
                              </Button>
                            </CardFooter>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-primary text-primary-foreground p-3 rounded-lg max-w-[85%]">
                    <p className="text-sm">{message.content}</p>
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted p-3 rounded-lg">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input */}
        <div className="border-t p-4 shrink-0">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Describe what documentation you need..."
              disabled={loading}
              className="flex-1"
            />
            <Button onClick={sendMessage} disabled={loading || !input.trim()}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Try: "Download all MUI documentation" or "Find comprehensive React docs"
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
